import React, { useEffect, useRef } from 'react';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { useTranslation } from 'react-i18next';
import { ensureDysGoEmbedScriptLoaded, cleanupPluginDOM } from '../utils/dysgoPlugin';

const PUNCTUATION_REGEX = /[.,!?;:()–—*\/£$%&=\^_§°]/; // includes comma, period, etc. + en/em dash
const GOOGLE_PLAY_URL = 'https://play.google.com/store';
const APP_STORE_URL = 'https://apps.apple.com/';

function useAnchorSmoothScroll() {
  useEffect(() => {
    const onClick = (e: Event) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      const isHash = href.startsWith('#');
      const isRootHash = href.startsWith('/#');
      if (!isHash && !isRootHash) return;

      const hash = isHash ? href : href.slice(1);
      const id = hash.startsWith('#') ? hash : `#${hash}`;
      const el = document.querySelector(id);
      if (!el) return;

      e.preventDefault();
      window.history.pushState(null, '', href);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);
}

const Landing: React.FC = () => {
  const dysgoLoaded = useRef(false);

  const renderPunctuated = (text: string) => {
    return Array.from(text).map((char, index) => {
      if (PUNCTUATION_REGEX.test(char)) {
        return (
          <span className="punctuation" key={`p-${index}`}>
            {char}
          </span>
        );
      }
      return char;
    });
  };

  useAnchorSmoothScroll();

  useEffect(() => {
    if (dysgoLoaded.current) return;
    dysgoLoaded.current = true;

    ensureDysGoEmbedScriptLoaded();

    // Init the plugin once the script loads
    const script = document.getElementById('dysgo-embed-js');
    if (script) {
      script.addEventListener('load', () => {
        window.DysGoEmbed?.init({
          targetLang: 'it',
          translatorVendor: 'google',
          mobile: 'auto',
          mouseoverTextType: 'sentence',
          excludeSelectors: ['nav', '.navbar', 'header'],
        });
      });
    }

    return () => {
      cleanupPluginDOM();
    };
  }, []);

  const { t } = useTranslation();
  const features = t('landing.features', { returnObjects: true }) as Array<{ title: string; description: string }>;

  return (
    <div id="static-root" className="landing-page">
      <style>{`
        /* Navy accent and orbs */
        .section-shaped .shape-primary { background: linear-gradient(150deg, var(--primary) 15%, #1B5694 70%, #052B57 94%) !important; }
        .section-shaped .shape-primary :nth-child(1) { background: #3f78b7 !important; }
        .section-shaped .shape-primary :nth-child(2) { background: #2e6aa8 !important; }
        .section-shaped .shape-primary :nth-child(3) { background: #2a5b8e !important; }
        .section-shaped .shape-primary :nth-child(4) { background: #1f6fb0 !important; }
        .section-shaped .shape-primary :nth-child(5) { background: #0f4a86 !important; }
        .section-shaped .shape-primary :nth-child(6) { background: #3f78b7 !important; }
        .section-shaped .shape-primary :nth-child(7) { background: #2e6aa8 !important; }
        .section-shaped .shape-primary :nth-child(8) { background: #2a5b8e !important; }
        .section-shaped .shape-primary :nth-child(9) { background: #1f6fb0 !important; }
        .section-shaped .shape-primary :nth-child(10) { background: #0f4a86 !important; }
        /* Make other purple bands use navy gradient as well */
        .landing-page .bg-gradient-primary { background: linear-gradient(150deg, var(--primary) 15%, #1B5694 70%, #052B57 94%) !important; }

        /* Responsive hero text scaling - scale container only to preserve em ratios */
        @media (max-width: 768px) {
          .striped-bg-hero { font-size: 1.9em !important; }
        }
        @media (max-width: 576px) {
          .striped-bg-hero { font-size: 1.8em !important; }
        }
      `}</style>

      <PublicNavbar />

      <section className="section section-hero section-shaped">
        <div className="shape shape-style-1 shape-primary">
          <span className="span-150"></span>
          <span className="span-50"></span>
          <span className="span-50"></span>
          <span className="span-75"></span>
          <span className="span-100"></span>
          <span className="span-75"></span>
          <span className="span-50"></span>
          <span className="span-100"></span>
          <span className="span-50"></span>
          <span className="span-100"></span>
        </div>
        <div className="page-header">
          <div className="container shape-container d-flex align-items-center py-lg">
            <div className="col px-0">
              <div className="row justify-content-center">
                <div className="col-lg-10 text-center">
                  <div className="striped-bg-hero mb-4">
                    <h1 className="striped-bg-hero-text">{renderPunctuated(t('landing.hero_title'))}</h1>
                  </div>
                  <p className="lead text-white">{t('landing.hero_subtitle')}</p>
                  <div className="btn-wrapper mt-4">
                    <a href="#download" className="btn btn-white btn-lg mr-2">
                      {t('landing.cta_get_app')}
                    </a>
                    <a href="/app/auth/login" className="btn btn-outline-white btn-lg">
                      {t('landing.cta_login')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="separator separator-bottom separator-skew zindex-100">
          <svg x="0" y="0" viewBox="0 0 2560 100" preserveAspectRatio="none" version="1.1" xmlns="http://www.w3.org/2000/svg">
            <polygon className="fill-white" points="2560 0 2560 100 0 100"></polygon>
          </svg>
        </div>
      </section>

      <section className="section" id="features">
        <div className="container">
          <div className="row">
            {features && features.length > 0 && (
              features.map((f, idx) => (
                <div className="col-md-4" key={idx}>
                  <div className="card shadow border-0 mb-4">
                    <div className="card-body text-center">
                      <div className="icon icon-shape bg-gradient-primary text-white rounded-circle mb-3">
                        <i className="ni ni-collection"></i>
                      </div>
                      <h5>{f.title}</h5>
                      <p className="text-muted mb-0">{f.description}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="section" id="demo">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 text-center">
              <h2 className="display-4 mb-3">{t('landing.demo_title', 'Try It Now')}</h2>
              <p className="lead text-muted mb-4">
                {t('landing.demo_subtitle', 'Hover over any text on this page to see DysGo in action. On mobile, just tap any word.')}
              </p>
              <div
                className="demo-content p-5 mb-3"
                style={{
                  background: '#f8f9fa',
                  borderRadius: '16px',
                  border: '2px dashed #dee2e6',
                  textAlign: 'left',
                  fontSize: '1.15rem',
                  lineHeight: '1.8',
                }}
              >
                <p style={{ marginBottom: '1rem' }}>
                  {t('landing.demo_text_1', 'The quick brown fox jumps over the lazy dog. This sentence contains every letter of the alphabet, making it a perfect test for reading tools.')}
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  {t('landing.demo_text_2', 'Reading should be accessible to everyone, regardless of how your brain processes text. DysGo transforms any content into a clean, dyslexia-friendly format with adjustable spacing, size, and highlighted punctuation.')}
                </p>
                <p style={{ marginBottom: 0 }}>
                  {t('landing.demo_text_3', 'Try hovering over these words to see the translation balloon appear. Press Alt to open the settings menu and customize font size, line height, and letter spacing in real time.')}
                </p>
              </div>
              <p className="text-muted small">
                <em>{t('landing.demo_hint', 'Hover over text = instant decoding. Press Alt = settings menu.')}</em>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-gradient-primary">
        <div className="container text-center">
          <div className="row justify-content-center">
            <div className="col-xl-8">
              <div className="display-4 text-white mb-3">"{t('landing.quote_text')}"</div>
              <div className="h5 text-white-50">{t('landing.quote_author')}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="about">
        <div className="container">
          <div className="row align-items-center justify-content-center justify-content-lg-between">
            <div className="col-12 col-lg-4">
              <h2 className="display-4 lh-1 mb-4">{t('landing.about_title')}</h2>
              <p className="lead text-muted mb-5 mb-lg-0">{t('landing.about_body')}</p>
            </div>
            <div className="col-sm-8 col-md-6">
              <div className="px-5 px-sm-0">
                <img className="img-fluid rounded-circle" src="/assets/img/taking-picture.jpg" alt="Empowered reading" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="section section-lg"
        id="events"
        style={{
          backgroundImage: 'url(/assets/img/kid2.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div className="container text-center">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h1
                className="display-3 text-white fw-bold mb-4"
                style={{
                  textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                  fontSize: '3.5rem',
                }}
              >
                {t('landing.tagline')}
              </h1>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-lg bg-gradient-primary" id="download">
        <div className="container text-center">
          <h2 className="text-white mb-4">{t('landing.download_title')}</h2>
          <a className="btn btn-white btn-lg mr-2" href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer">
            <img src="/assets/img/google-play-badge.svg" alt={t('landing.download_google_alt')} style={{ height: 32 }} />
          </a>
          <a className="btn btn-white btn-lg" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img src="/assets/img/app-store-badge.svg" alt={t('landing.download_appstore_alt')} style={{ height: 32 }} />
          </a>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default Landing;

